import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import {
  useGetTenantForSchoolAdminQuery,
  useUpdateTenantSettingsMutation,
  useUploadFaviconMutation,
  useParseAndSaveSamlMetadataMutation,
  useGetContentSafetySettingsQuery,
  useUpdateContentSafetySettingsMutation,
  useGetNudgeTypeConfigsQuery,
  useSetNudgeTypeEnabledMutation,
  NudgeType,
} from "@axio/graphql/generated/graphql";

import { ArrowCounterClockwise } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { Check } from "@phosphor-icons/react/dist/csr/Check";
import { Palette } from "@phosphor-icons/react/dist/csr/Palette";
import { ChatCircle } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { ShieldCheck } from "@phosphor-icons/react/dist/csr/ShieldCheck";
import { BellRinging } from "@phosphor-icons/react/dist/csr/BellRinging";
import { Link2 } from "lucide-react";
import NewCustomAxioActionModal from "../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import AdminTabNavigator from "../../components/adminDashboard/AdminTabNavigator/AdminTabNavigator";
import AdminBrandingSettingsContainer from "../../containers/adminDashboard/AdminBrandingSettingsContainer";
import AdminCompanionSettingsContainer from "../../containers/adminDashboard/AdminCompanionSettingsContainer";
import { useAppConfig } from "../../AppConfigProvider";
import AdminContentSafetySettingsContainer from "../../containers/adminDashboard/AdminContentSafetySettingsContainer";
import AdminLtiSettingsContainer from "../../containers/adminDashboard/AdminLtiSettingsContainer";
import {
  NudgeSettings,
  allNudgeTypes,
  nudgeCategoryDefinitions,
  type NudgeTypeSettings,
} from "../../components/adminDashboard/NudgeSettings";

const buildNudgeTypeKeys = (tenantId: string) => [
  ...allNudgeTypes.map((nudgeType) => `GLOBAL:${nudgeType}`),
  ...allNudgeTypes.map((nudgeType) => `TENANT:${tenantId}:${nudgeType}`),
];

const createDefaultNudgeTypeSettings = (): NudgeTypeSettings =>
  allNudgeTypes.reduce(
    (acc, nudgeType) => ({
      ...acc,
      [nudgeType]: false,
    }),
    {} as NudgeTypeSettings
  );

const resolveNudgeTypeSettings = (
  configs: Array<{ key: string; nudgeType: NudgeType; enabled: boolean }>,
  tenantId: string
): NudgeTypeSettings => {
  const resolved = createDefaultNudgeTypeSettings();

  allNudgeTypes.forEach((nudgeType) => {
    const tenantKey = `TENANT:${tenantId}:${nudgeType}`;
    const globalKey = `GLOBAL:${nudgeType}`;
    const tenantConfig = configs.find((config) => config.key === tenantKey);
    const globalConfig = configs.find((config) => config.key === globalKey);

    resolved[nudgeType] = tenantConfig?.enabled ?? globalConfig?.enabled ?? false;
  });

  return resolved;
};

const formatSavedTimestamp = (date: Date) =>
  date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

function AdminSettingsView() {
  const { t } = useTranslation();
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const defaultActiveTab = "branding";
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const companionOnlyTabs = ["aiCompanion", "contentSafety"];
  const activeTabForMode = !isCompanionMode && companionOnlyTabs.includes(activeTab) ? defaultActiveTab : activeTab;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSamlMetadataModal, setShowSamlMetadataModal] = useState(false);
  const [samlMetadataUrl, setSamlMetadataUrl] = useState("");

  const [buttonLabel, setButtonLabel] = useState("Fetch & Save");
  const [parsedMetadata, setParsedMetadata] = useState<{
    entityId: string;
    ssoUrl: string;
    cert: string;
    expiry: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    logo: "",
    favicon: "",
    primaryColor: "",
    financialInformationUrl: "",
    helpCenterUrl: "",
    schoolResourcesUrl: "",
    officialTranscriptsUrl: "",
  });

  const [originalData, setOriginalData] = useState(formData);

  const [contentSafety, setContentSafety] = useState({
    sexual: true,
    hate: true,
    harassment: true,
    selfHarm: true,
    violence: true,
    withdrawal: false,
    ada: false,
  });

  const [originalContentSafety, setOriginalContentSafety] = useState(contentSafety);

  const defaultEmailsState = {
    sexual: "",
    hate: "",
    harassment: "",
    selfHarm: "",
    violence: "",
    withdrawal: "",
    ada: "",
  };

  const [contentSafetyEmails, setContentSafetyEmails] = useState(defaultEmailsState);
  const [originalContentSafetyEmails, setOriginalContentSafetyEmails] = useState(defaultEmailsState);

  const [nudgeTypeSettings, setNudgeTypeSettings] = useState<NudgeTypeSettings>(createDefaultNudgeTypeSettings());
  const [originalNudgeTypeSettings, setOriginalNudgeTypeSettings] = useState<NudgeTypeSettings>(
    createDefaultNudgeTypeSettings()
  );
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const { data: contentSafetyData, refetch: refetchContentSafety } = useGetContentSafetySettingsQuery();

  const [updateContentSafetySettings] = useUpdateContentSafetySettingsMutation();
  const [setNudgeTypeEnabled] = useSetNudgeTypeEnabledMutation();

  const {
    data: tenantData,
    loading: getTenantLoading,
    error: getTenantError,
    refetch: refetchTenant,
  } = useGetTenantForSchoolAdminQuery();

  const [updateTenantSettings] = useUpdateTenantSettingsMutation();
  const [uploadFavicon] = useUploadFaviconMutation();

  const tenant = tenantData?.getTenantForSchoolAdmin?.data?.[0];

  const { data: nudgeTypeConfigData, refetch: refetchNudgeTypeConfigs } = useGetNudgeTypeConfigsQuery({
    skip: !tenant?.id,
    variables: {
      keys: tenant?.id ? buildNudgeTypeKeys(tenant.id) : [],
    },
    fetchPolicy: "network-only",
  });

  const [parseAndSaveSamlMetadata] = useParseAndSaveSamlMetadataMutation();

  useEffect(() => {
    if (activeTab !== activeTabForMode) {
      setActiveTab(activeTabForMode);
    }
  }, [activeTab, activeTabForMode]);

  const handleSamlMetadataSubmit = async () => {
    if (!samlMetadataUrl) return setErrorMessage("Please enter a metadata URL");

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { data } = await parseAndSaveSamlMetadata({
        variables: { metadataUrl: samlMetadataUrl },
      });

      const response = data?.parseAndSaveSamlMetadata;

      if (!response?.success) throw new Error(response?.message);
      setParsedMetadata({
        entityId: response.data.issuer,
        ssoUrl: response.data.entryPoint,
        cert: response.data.cert,
        expiry: response.data.expiry || "",
      });
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to import metadata");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoAfterUpload = (logoUrl: string) => {
    updateFormData("logo", logoUrl);
    refetchTenant();
  };

  const handleFaviconUpload = async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg";

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          await handleFaviconFileUpload(file);
        }
      };

      input.click();
    } catch (error) {
      setErrorMessage("Failed to open file picker");
      setIsSubmitting(false);
    }
  };

  const handleFaviconFileUpload = async (file: File) => {
    if (file.size > 1 * 1024 * 1024) {
      setErrorMessage("Favicon file size should not exceed 1MB");
      return;
    }

    const acceptedTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon", "image/svg+xml", "image/jpeg"];
    if (!acceptedTypes.includes(file.type)) {
      setErrorMessage("Only PNG, ICO, SVG, and JPG files are allowed for favicon");
      return;
    }

    // Validate dimensions for raster files; skip SVG and ICO aliases
    if (file.type !== "image/svg+xml" && file.type !== "image/x-icon" && file.type !== "image/vnd.microsoft.icon") {
      const img = new Image();
      img.onload = async () => {
        if (img.width < 16 || img.width > 1024 || img.height < 16 || img.height > 1024) {
          setErrorMessage("Favicon dimensions must be between 16×16 and 1024×1024 pixels");
          return;
        }
        if (img.width !== img.height) {
          setErrorMessage("Favicon must be square (1:1 aspect ratio)");
          return;
        }
        await processFaviconUpload(file);
      };
      img.onerror = () => {
        setErrorMessage("Invalid image file");
      };
      img.src = URL.createObjectURL(file);
    } else if (file.type === "image/x-icon" || file.type === "image/vnd.microsoft.icon") {
      await uploadIcoAsPng(file);
    } else {
      // For SVG files, skip dimension validation
      await processFaviconUpload(file);
    }
  };

  const uploadIcoAsPng = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const base64Data: string = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          try {
            const targetSize = 180;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const side = Math.min(img.width, img.height) || targetSize;
            canvas.width = targetSize;
            canvas.height = targetSize;
            if (!ctx) {
              reject(new Error("Canvas is not supported"));
              URL.revokeObjectURL(objectUrl);
              return;
            }
            const sx = Math.max(0, (img.width - side) / 2);
            const sy = Math.max(0, (img.height - side) / 2);
            ctx.drawImage(img, sx, sy, side, side, 0, 0, targetSize, targetSize);
            const dataUrl = canvas.toDataURL("image/png");
            resolve(dataUrl.split(",")[1]);
          } catch (err) {
            reject(err);
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Invalid ICO image"));
        };
        img.src = objectUrl;
      });

      // approximate byte size of base64 payload
      const estimatedSize = Math.ceil((base64Data.length * 3) / 4);
      const result = await uploadFavicon({
        variables: {
          favicon: base64Data,
          contentType: "image/png",
          size: estimatedSize,
        },
      });

      if (result.data?.uploadFavicon?.success) {
        const faviconUrl = result.data.uploadFavicon.data;
        updateFormData("favicon", faviconUrl);
        refetchTenant();
      } else {
        throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
      }
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to upload favicon");
    } finally {
      setIsSubmitting(false);
    }
  };

  const processFaviconUpload = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(",")[1];

        const result = await uploadFavicon({
          variables: {
            favicon: base64Data,
            contentType: file.type,
            size: file.size,
          },
        });

        if (result.data?.uploadFavicon?.success) {
          const faviconUrl180 = result.data.uploadFavicon.data;
          updateFormData("favicon", faviconUrl180);
          refetchTenant();
        } else {
          throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
        }
        setIsSubmitting(false);
      };
      reader.onerror = () => {
        setErrorMessage("Failed to read file");
        setIsSubmitting(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setErrorMessage(error.message || "Failed to upload favicon");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const settings = contentSafetyData?.getContentSafetySettings?.data?.[0];
    if (settings) {
      const cleaned = {
        sexual: settings.sexual,
        hate: settings.hate,
        harassment: settings.harassment,
        selfHarm: settings.selfHarm,
        violence: settings.violence,
        withdrawal: settings.withdrawal,
        ada: settings.ada,
      };

      const emails = {
        sexual: (settings.sexualEmails ?? []).join(", "),
        hate: (settings.hateEmails ?? []).join(", "),
        harassment: (settings.harassmentEmails ?? []).join(", "),
        selfHarm: (settings.selfHarmEmails ?? []).join(", "),
        violence: (settings.violenceEmails ?? []).join(", "),
        withdrawal: (settings.withdrawalEmails ?? []).join(", "),
        ada: (settings.adaEmails ?? []).join(", "),
      };

      setContentSafety(cleaned);
      setOriginalContentSafety(cleaned);
      setContentSafetyEmails(emails);
      setOriginalContentSafetyEmails(emails);
    }
  }, [contentSafetyData]);

  useEffect(() => {
    if (!tenant?.id) {
      return;
    }

    const configs = nudgeTypeConfigData?.getNudgeTypeConfigs?.data ?? [];
    if (configs.length === 0) {
      return;
    }

    const resolvedSettings = resolveNudgeTypeSettings(
      configs
        .filter((config): config is NonNullable<typeof config> => Boolean(config))
        .map((config) => ({
          key: config.key,
          nudgeType: config.nudgeType,
          enabled: config.enabled,
        })),
      tenant.id
    );

    setNudgeTypeSettings(resolvedSettings);
    setOriginalNudgeTypeSettings(resolvedSettings);
  }, [nudgeTypeConfigData, tenant?.id]);

  // Initialize form data when tenant data loads
  useEffect(() => {
    if (tenant) {
      const initialData = {
        logo: tenant.logo || "",
        favicon: tenant.favicon180Url || tenant.favicon16Url || "",
        primaryColor: tenant.primaryColor || "#434756",
        financialInformationUrl: tenant.financialInformationUrl || "",
        helpCenterUrl: tenant.helpCenterUrl || "",
        schoolResourcesUrl: tenant.schoolResourcesUrl || "",
        officialTranscriptsUrl: tenant.officialTranscriptsUrl || "",
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [tenant]);

  // Check for changes
  useEffect(() => {
    const hasBrandingChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    const hasContentSafetyChanges =
      JSON.stringify(contentSafety) !== JSON.stringify(originalContentSafety) ||
      JSON.stringify(contentSafetyEmails) !== JSON.stringify(originalContentSafetyEmails);
    const hasNudgeChanges = JSON.stringify(nudgeTypeSettings) !== JSON.stringify(originalNudgeTypeSettings);

    if (activeTabForMode === "branding") {
      setHasChanges(hasBrandingChanges);
      return;
    }

    if (activeTabForMode === "contentSafety") {
      setHasChanges(hasContentSafetyChanges);
      return;
    }

    if (activeTabForMode === "nudgeSettings") {
      setHasChanges(hasNudgeChanges);
      return;
    }

    setHasChanges(false);
  }, [
    activeTabForMode,
    formData,
    originalData,
    contentSafety,
    originalContentSafety,
    contentSafetyEmails,
    originalContentSafetyEmails,
    nudgeTypeSettings,
    originalNudgeTypeSettings,
  ]);

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrorMessage("");
  };

  const validateHexColor = (color: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const handleColorChange = (color: string) => {
    if (!color.startsWith("#")) {
      color = "#" + color;
    }
    updateFormData("primaryColor", color);
  };

  const handleSaveBranding = async () => {
    if (!validateHexColor(formData.primaryColor)) {
      throw new Error("Please enter a valid hex color code (e.g., #434756)");
    }

    const result = await updateTenantSettings({
      variables: {
        settings: {
          primaryColor: formData.primaryColor,
          financialInformationUrl: formData.financialInformationUrl,
          helpCenterUrl: formData.helpCenterUrl,
          schoolResourcesUrl: formData.schoolResourcesUrl,
          officialTranscriptsUrl: formData.officialTranscriptsUrl,
          logo: formData.logo,
        },
      },
    });

    if (!result.data?.updateTenantSettings?.success) {
      throw new Error(result.data?.updateTenantSettings?.message || "Failed to save branding settings");
    }

    setOriginalData(formData);
    setLastSavedAt(formatSavedTimestamp(new Date()));
    await refetchTenant();
  };

  const handleSaveContentSafety = async () => {
    const parseEmails = (csv: string): string[] =>
      csv
        .split(",")
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e.length > 0);

    const sexualEmails = parseEmails(contentSafetyEmails.sexual);
    const hateEmails = parseEmails(contentSafetyEmails.hate);
    const harassmentEmails = parseEmails(contentSafetyEmails.harassment);
    const selfHarmEmails = parseEmails(contentSafetyEmails.selfHarm);
    const violenceEmails = parseEmails(contentSafetyEmails.violence);
    const withdrawalEmails = parseEmails(contentSafetyEmails.withdrawal);
    const adaEmails = parseEmails(contentSafetyEmails.ada);

    // If a category previously had recipients and they were all removed, disable that category
    const resolvedToggles = {
      sexual:
        contentSafety.sexual && originalContentSafetyEmails.sexual.trim() !== "" && sexualEmails.length === 0
          ? false
          : contentSafety.sexual,
      hate:
        contentSafety.hate && originalContentSafetyEmails.hate.trim() !== "" && hateEmails.length === 0
          ? false
          : contentSafety.hate,
      harassment:
        contentSafety.harassment &&
        originalContentSafetyEmails.harassment.trim() !== "" &&
        harassmentEmails.length === 0
          ? false
          : contentSafety.harassment,
      selfHarm:
        contentSafety.selfHarm && originalContentSafetyEmails.selfHarm.trim() !== "" && selfHarmEmails.length === 0
          ? false
          : contentSafety.selfHarm,
      violence:
        contentSafety.violence && originalContentSafetyEmails.violence.trim() !== "" && violenceEmails.length === 0
          ? false
          : contentSafety.violence,
      withdrawal:
        contentSafety.withdrawal &&
        originalContentSafetyEmails.withdrawal.trim() !== "" &&
        withdrawalEmails.length === 0
          ? false
          : contentSafety.withdrawal,
      ada:
        contentSafety.ada && originalContentSafetyEmails.ada.trim() !== "" && adaEmails.length === 0
          ? false
          : contentSafety.ada,
    };

    const result = await updateContentSafetySettings({
      variables: {
        settings: {
          ...resolvedToggles,
          sexualEmails,
          hateEmails,
          harassmentEmails,
          selfHarmEmails,
          violenceEmails,
          withdrawalEmails,
          adaEmails,
        },
      },
    });

    if (!result.data?.updateContentSafetySettings?.success) {
      throw new Error(result.data?.updateContentSafetySettings?.message || "Failed to save content safety settings");
    }

    setContentSafety(resolvedToggles);
    setOriginalContentSafety(resolvedToggles);
    setOriginalContentSafetyEmails(contentSafetyEmails);
    setLastSavedAt(formatSavedTimestamp(new Date()));
    await refetchContentSafety();
  };

  const handleSaveNudges = async () => {
    if (!tenant?.id) {
      throw new Error("Tenant ID is required");
    }

    const results = await Promise.all(
      allNudgeTypes.map((nudgeType) =>
        setNudgeTypeEnabled({
          variables: {
            nudgeType,
            enabled: nudgeTypeSettings[nudgeType],
          },
        })
      )
    );

    const failedMutation = results.find((result) => !result.data?.setNudgeTypeEnabled?.success);
    if (failedMutation) {
      throw new Error(failedMutation.data?.setNudgeTypeEnabled?.message || "Failed to save nudge settings");
    }

    setOriginalNudgeTypeSettings(nudgeTypeSettings);
    setLastSavedAt(formatSavedTimestamp(new Date()));
    await refetchNudgeTypeConfigs();
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      if (activeTabForMode === "branding") {
        await handleSaveBranding();
      } else if (activeTabForMode === "contentSafety") {
        await handleSaveContentSafety();
      } else if (activeTabForMode === "nudgeSettings") {
        await handleSaveNudges();
      }
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to save settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (activeTabForMode === "branding") {
      setFormData(originalData);
    } else if (activeTabForMode === "contentSafety") {
      setContentSafety(originalContentSafety);
      setContentSafetyEmails(originalContentSafetyEmails);
    } else if (activeTabForMode === "nudgeSettings") {
      setNudgeTypeSettings(originalNudgeTypeSettings);
    }

    setHasChanges(false);
    setErrorMessage("");
  };

  const getFaviconPreviewUrl = (): string => {
    const candidate =
      tenant?.favicon180Url ||
      tenant?.favicon16Url ||
      (formData.favicon ? `${formData.favicon}/favicon-180x180.png?alt=media` : "");
    if (!candidate) return "";
    const sep = candidate.includes("?") ? "&" : "?";
    return `${candidate}${sep}t=${Date.now()}`;
  };

  const tabData = [
    { key: "branding", title: "Branding", icon: <Palette size={20} weight="regular" /> },
    ...(isCompanionMode
      ? [{ key: "aiCompanion", title: "AI Knowledge Base", icon: <ChatCircle size={20} weight="regular" /> }]
      : []),
    { key: "ltiSettings", title: t("buttons.lti_integration"), icon: <Link2 size={20} /> },
    ...(isCompanionMode
      ? [{ key: "contentSafety", title: "Content Safety", icon: <ShieldCheck size={20} weight="regular" /> }]
      : []),
    { key: "nudgeSettings", title: "Nudge Settings", icon: <BellRinging size={20} weight="regular" /> },
  ];

  if (getTenantLoading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (getTenantError) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Failed to load settings" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}>
            <View>
              <CustomText
                text="Settings"
                size="m"
                useTranslationText={false}
                textType="display"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />

              <CustomSpacing type="vertical" size="s" />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <CustomText
                  text="Configure your institution's branding, authentication, and AI companion settings."
                  size="m"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
                <CustomButton
                  text="Upload SAML Metadata URL"
                  size="s"
                  styleType="primary"
                  onPress={() => setShowSamlMetadataModal(true)}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            <AdminTabNavigator tabData={tabData} activeTab={activeTabForMode} onTabPress={setActiveTab}>
              {(currentTab) => {
                if (currentTab === "branding") {
                  return (
                    <AdminBrandingSettingsContainer
                      formData={formData}
                      updateFormData={updateFormData}
                      handleLogoAfterUpload={handleLogoAfterUpload}
                      handleFaviconUpload={handleFaviconUpload}
                      handleColorChange={handleColorChange}
                      validateHexColor={validateHexColor}
                      getFaviconPreviewUrl={getFaviconPreviewUrl}
                      isSubmitting={isSubmitting}
                      tenantPrimaryColor={tenant?.primaryColor}
                      tenantFavicon180Url={tenant?.favicon180Url}
                    />
                  );
                } else if (currentTab === "ltiSettings") {
                  return (
                    <AdminLtiSettingsContainer
                      ltiActivitiesEnabled={tenant?.ltiActivitiesEnabled}
                      tenantDomain={tenant?.tenantDomain}
                      onUpdateSettings={updateTenantSettings}
                    />
                  );
                } else if (currentTab === "aiCompanion") {
                  return <AdminCompanionSettingsContainer />;
                } else if (currentTab === "contentSafety") {
                  return (
                    <AdminContentSafetySettingsContainer
                      contentSafety={contentSafety}
                      setContentSafety={setContentSafety}
                      emailsState={contentSafetyEmails}
                      setEmailsState={setContentSafetyEmails}
                    />
                  );
                } else if (currentTab === "nudgeSettings") {
                  return (
                    <NudgeSettings
                      categories={nudgeCategoryDefinitions}
                      nudgeTypeSettings={nudgeTypeSettings}
                      onToggleType={(type, enabled) => {
                        setNudgeTypeSettings((prev) => ({
                          ...prev,
                          [type]: enabled,
                        }));
                      }}
                    />
                  );
                }
                return null;
              }}
            </AdminTabNavigator>

            {errorMessage && (
              <View style={styles.errorMessageContainer}>
                <CustomText text={errorMessage} size="s" useTranslationText={false} style={{ color: "#EF4444" }} />
              </View>
            )}

            <CustomSpacing type="vertical" size="xl" />

            {activeTabForMode !== "aiCompanion" && (
              <View style={styles.footerActions}>
                <View style={styles.lastSaved}>
                  <CustomText
                    text={lastSavedAt ? `Last saved: Today at ${lastSavedAt}` : "Last saved: Not yet saved"}
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#667085" }}
                  />
                </View>

                <View style={styles.actionButtons}>
                  <CustomButton
                    text="Reset Changes"
                    styleType="primaryLight"
                    size="m"
                    bold={true}
                    onPress={handleReset}
                    disabled={!hasChanges || isSubmitting}
                    useTranslationText={false}
                    style={(!hasChanges || isSubmitting) && styles.disabledButton}
                    textStyle={{ color: "#3E68FE" }}
                    leftIcon={<ArrowCounterClockwise size={24} color="#3E68FE" />}
                  />
                  <CustomButton
                    text={isSubmitting ? "Saving..." : "Save Settings"}
                    styleType="primary"
                    size="m"
                    bold={true}
                    onPress={handleSave}
                    disabled={!hasChanges || isSubmitting}
                    useTranslationText={false}
                    style={(!hasChanges || isSubmitting) && styles.disabledButton}
                    leftIcon={<Check size={24} color="#FFFFFF" />}
                  />
                </View>
              </View>
            )}
          </MotiView>
        </View>
      </View>
      <NewCustomAxioActionModal
        visible={showSamlMetadataModal}
        onClose={() => {
          setShowSamlMetadataModal(false);
          setSamlMetadataUrl("");
          setParsedMetadata(null);
          setErrorMessage("");
          setButtonLabel("Save");
        }}
        onAction={async () => {
          await handleSamlMetadataSubmit();

          if (parsedMetadata) {
            setButtonLabel("Saved");
            setTimeout(() => setButtonLabel("Fetch & Save"), 3000);
          }
        }}
        modalTexts={{
          title: "Import SAML Metadata",
          ariaLabel: "saml-metadata-upload",
          warningText: "Paste your Identity Provider’s metadata URL below.",
          actionButtonText: parsedMetadata ? "Saved" : "Fetch & Save",
        }}>
        <CustomTextInput
          value={samlMetadataUrl}
          onChangeText={setSamlMetadataUrl}
          placeholder="https://idp.example.com/metadata.xml"
          style={{ marginBottom: 24 }}
        />
        {parsedMetadata && (
          <View style={{ marginTop: 16 }}>
            <CustomText useTranslationText={false} text="Metadata Parsed" weight="bold" style={{ marginBottom: 12 }} />

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="Entity ID: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.entityId} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="SSO URL: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.ssoUrl} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText useTranslationText={false} text="Cert Expires: " size="s" weight="medium" />

              <CustomText
                useTranslationText={false}
                text={parsedMetadata.expiry}
                size="xs"
                style={{
                  color: "#6B7280",
                  marginTop: 2,
                }}
              />
            </View>

            <View style={{ marginBottom: 12 }}>
              <CustomText useTranslationText={false} text="Certificate: " size="s" weight="medium" />
              <CustomText
                useTranslationText={false}
                text={parsedMetadata.cert}
                size="xs"
                style={{ color: "#6B7280", marginTop: 2 }}
              />
            </View>
          </View>
        )}

        {errorMessage && (
          <CustomText text={errorMessage} size="s" style={{ color: "red", marginTop: 12, marginBottom: 12 }} />
        )}
      </NewCustomAxioActionModal>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
    maxWidth: 1280,
    width: "100%",
    alignSelf: "center",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorMessageContainer: {
    backgroundColor: "#FEF2F2",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FECACA",
    marginTop: 16,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  lastSaved: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 12,
  },
  disabledButton: {
    opacity: 0.5,
  },
});

export default AdminSettingsView;
